import { markRaw } from 'vue';
import { importTypes } from '@rancher/auto-import';
import {
  ActionLocation, ActionOpts, IPlugin, OnNavAwayFromPackage, OnNavToPackage
} from '@shell/core/types';
import { EPINIO_TYPES } from './types';
import epinioRoutes from './routing/epinio-routing';

import epinioMgmtStore from './store/epinio-mgmt-store';
import epinioStore from './store/epinio-store';
import { createEpinioRoute } from './utils/custom-routing';
import { mountDock, unmountDock } from './utils/dock-mount';
import './assets/overrides.scss';

// import trailhand styles and components
import '@krumio/trailhand-ui';

const epinioObjAnnotations = [
  'epinio.io/app-container',
  'epinio.io/created-by'
];

// hide warnings related to trailhand components, they are working fine, however we are unable to register them gloabally due to the rancher shell
const _warn = console.warn;  
console.warn = (...args: any[]) => {  
  if (typeof args[0] === 'string' && args[0].includes('Failed to resolve component: trailhand-')) {
    return;
  }
  _warn(...args);
};

const isPodFromEpinio = (a: string) => epinioObjAnnotations.includes(a);

const onEnter: OnNavToPackage = async(store) => {
  const { getters, dispatch } = store;

  await dispatch(`${ epinioMgmtStore.config.namespace }/loadManagement`);

  if (getters['isSingleProduct']) {
    dispatch(`${ epinioStore.config.namespace }/info`); // We can get this in the background
  }

  // Mount EpinioDock (app logs/shell panel) appended to document.body rather than
  // patching it into Shell's own root chrome, see dock-mount.ts. Guarded internally
  // so re-entering the product on subsequent navigations is a no-op.
  mountDock(store);
};

const onLeave: OnNavAwayFromPackage = async(store) => {
  // The dashboard retains the previous cluster info until another cluster is loaded, this helps when returning to the same cluster.
  // We need to forget epinio cluster info
  // - The polling is pretty brutal
  // - The nav path through to the same epinio cluster is fraught with danger (nav from previous cluster id to blank cluster, required to switch epinio clusters)
  await store.dispatch(`${ epinioStore.config.namespace }/unsubscribe`);
  await store.commit(`${ epinioStore.config.namespace }/reset`);

  // Remove the dock (app logs/shell panel) so it doesn't linger 
  unmountDock();
};

// Init the package
export default function(plugin: IPlugin) {
  // Auto-import model, detail, edit from the folders
  importTypes(plugin);

  // Provide plugin metadata from package.json
  plugin.metadata = require('./package.json'); // eslint-disable-line @typescript-eslint/no-require-imports

  // Load a product
  plugin.addProduct(require('./config/epinio')); // eslint-disable-line @typescript-eslint/no-require-imports
  // plugin.addProduct(require('./product'));

  // Add Vuex stores
  plugin.addDashboardStore(
    epinioMgmtStore.config.namespace,
    epinioMgmtStore.specifics,
    epinioMgmtStore.config,
  );
  plugin.addDashboardStore(
    epinioStore.config.namespace,
    epinioStore.specifics,
    epinioStore.config,
  );

  // Add Vue Routes
  plugin.addRoutes(epinioRoutes);

  // Add theme toggle to header (markRaw avoids Vue reactive-object warning)
  const ThemeToggle = require('./components/ThemeToggle.vue'); // eslint-disable-line @typescript-eslint/no-require-imports
  plugin.register('component', 'NavHeaderRight', markRaw(ThemeToggle.default || ThemeToggle));

  // Add hooks to Vue navigation world
  plugin.addNavHooks(onEnter, onLeave);

  // Register unsaved changes dialog (markRaw avoids Vue reactive-object warning)
  const UnsavedChangesDialog = require('./dialog/UnsavedChangesDialog.vue'); // eslint-disable-line @typescript-eslint/no-require-imports
  plugin.register('component', 'UnsavedChangesDialog', markRaw(UnsavedChangesDialog.default || UnsavedChangesDialog));

  // Register install Epinio dialog
  const InstallDialog = require('./dialog/InstallDialog.vue'); // eslint-disable-line @typescript-eslint/no-require-imports
  plugin.register('component', 'InstallDialog', InstallDialog.default || InstallDialog);

  // Add action button in the menu of each object belonging to Epinio's applications
  plugin.addAction(
    ActionLocation.TABLE,
    {
      resource: [
        'apps.deployment',
        // 'batch.job',
        'pod',
        // 'service',
        'workload'
      ]
    },
    {
      labelKey: 'epinio.applications.actions.goToEpinio.label',
      icon:     'icon-epinio',
      enabled(ctx: any) {
        return !!Object.keys(ctx.metadata.annotations || []).find((annotation) => isPodFromEpinio(annotation));
      },
      invoke(_: ActionOpts, values: any[]) {
        const obj = values[0];
        const $router = obj.$rootState.$router;

        const epinioNamespace = obj.labels['app.kubernetes.io/part-of'];
        const epinioAppName = obj.labels['app.kubernetes.io/name'];

        $router.replace(createEpinioRoute(`c-cluster-resource-id`, {
          cluster:  obj.$rootGetters['clusterId'],
          resource: EPINIO_TYPES.APP,
          id:       `${ epinioNamespace }/${ epinioAppName }`
        }));
      }
    }
  );
}
